import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Home, ChevronRight, Sparkles, Loader2, Copy, Check, CalendarDays,
  Wand2, RefreshCw, Play, Edit3, Save, X, Layers, Languages, Hash,
  AlertCircle, ListChecks, Trash2, Plus,
} from "lucide-react";
import { AdminSubnav } from "./AdminSubnav";
import { adminFetch } from "../lib/auth";

// --- Types ---

interface Question {
  id: number;
  question: string;
  options: string[];
  correctAnswer: number;
}

type Granularity = "weekly" | "daily" | "monthly" | "custom";

interface Session {
  id: string;
  label: string;
  title: string;
  topic: string;
  weekNumber?: number;
  date?: string;
  month?: number;
  year?: number;
  language?: string;
  difficulty?: string;
  generated: boolean;
  questionSetId: string | null;
}

interface Schedule {
  userId: string;
  userName: string;
  createdAt: string | null;
  updatedAt: string | null;
  granularity: Granularity;
  language: string;
  difficulty: string;
  questionsPerGame: number;
  timePerQuestion: number;
  sessions: Session[];
  exists?: boolean;
}

const LANGUAGES = ["French", "English", "German", "Luxembourgish", "Portuguese"];
const DIFFICULTIES = ["easy", "medium", "hard"];

// Nombre de sessions par défaut selon la granularité — couvre une durée "naturelle"
// (1 an de cadence hebdo/mensuelle, ~1 mois en daily, 10 dates en custom).
const DEFAULT_COUNT_BY_GRANULARITY: Record<Granularity, number> = {
  weekly: 52,
  monthly: 12,
  daily: 30,
  custom: 10,
};

// --- Composant principal ---

export function AdminSchedule() {
  const [schedule, setSchedule] = useState<Schedule | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadSchedule = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminFetch("/api/admin/my-schedule");
      if (res?.ok) setSchedule(await res.json());
    } catch {
      setError("Failed to load schedule");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSchedule();
  }, [loadSchedule]);

  if (loading) {
    return (
      <ScheduleShell>
        <div className="flex flex-col items-center justify-center py-20">
          <Loader2 className="w-10 h-10 text-sakura-500 animate-spin mb-3" />
          <p className="text-lavender-400 font-semibold">Loading your schedule…</p>
        </div>
      </ScheduleShell>
    );
  }

  const isEmpty = !schedule?.exists || schedule.sessions.length === 0;

  return (
    <ScheduleShell>
      {error && (
        <div className="bg-red-50 border-2 border-red-200 text-red-600 rounded-xl px-4 py-3 mb-4 flex items-center gap-2 text-sm font-semibold">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      )}
      {isEmpty || !schedule ? (
        <Wizard onCreated={loadSchedule} setError={setError} />
      ) : (
        <Editor schedule={schedule} onChanged={loadSchedule} setError={setError} />
      )}
    </ScheduleShell>
  );
}

// --- Shell (header / breadcrumb / bg) ---

function ScheduleShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen font-kawaii bg-gradient-to-br from-sakura-100 via-lavender-100 to-sky-100 relative overflow-hidden">
      <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] rounded-full bg-sakura-200/40 blur-3xl pointer-events-none" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[400px] h-[400px] rounded-full bg-lavender-200/40 blur-3xl pointer-events-none" />

      <div className="relative z-10 max-w-4xl mx-auto p-4 py-8">
        <motion.nav
          initial={{ y: -10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="mb-6 flex items-center gap-1 text-sm font-bold"
        >
          <Link
            to="/admin"
            className="flex items-center gap-1 text-lavender-400 hover:text-sakura-400 transition-colors"
          >
            <Home className="w-4 h-4" />
            Admin
          </Link>
          <ChevronRight className="w-4 h-4 text-lavender-300" />
          <span className="text-sakura-500 flex items-center gap-1">
            <CalendarDays className="w-4 h-4" />
            My Schedule
          </span>
        </motion.nav>

        <AdminSubnav />

        {children}
      </div>
    </div>
  );
}

// --- Wizard (état vide) ---

interface WizardProps {
  onCreated: () => void;
  setError: (e: string) => void;
}

function Wizard({ onCreated, setError }: WizardProps) {
  const [theme, setTheme] = useState("");
  const [granularity, setGranularity] = useState<Granularity>("weekly");
  const [count, setCount] = useState(DEFAULT_COUNT_BY_GRANULARITY.weekly);
  // Si l'utilisateur a touché manuellement le compteur, on ne réécrit plus à chaque changement
  // de granularité — sinon, on suit le default "naturel".
  const [countDirty, setCountDirty] = useState(false);
  const [language, setLanguage] = useState("English");
  const [difficulty, setDifficulty] = useState("medium");
  // Anchor de départ (selon granularité)
  const [startWeek, setStartWeek] = useState<number>(getCurrentIsoWeek());
  const [startDate, setStartDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [startMonth, setStartMonth] = useState<number>(new Date().getMonth() + 1);

  const [generating, setGenerating] = useState(false);

  const handleGenerate = async () => {
    if (!theme.trim()) {
      setError("Please enter a theme first");
      return;
    }
    setError("");
    setGenerating(true);
    try {
      const body: Record<string, unknown> = {
        theme: theme.trim(),
        count,
        granularity,
        language,
        difficulty,
      };
      if (granularity === "weekly") body.startWeek = startWeek;
      if (granularity === "monthly") body.startMonth = startMonth;
      if (granularity === "daily" || granularity === "custom") body.startDate = startDate;

      const res = await adminFetch("/api/admin/my-schedule/generate-outline", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res) return; // session expirée : nav vers Authentik en cours
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.details || err.error || "Outline generation failed");
      }
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <motion.div
      initial={{ y: 10, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className="bg-white/85 backdrop-blur-xl rounded-blob shadow-kawaii-lg border-2 border-sakura-200/50 p-7"
    >
      <div className="flex items-center gap-3 mb-2">
        <Wand2 className="w-7 h-7 text-sakura-500" />
        <h2 className="text-2xl font-black text-sakura-500">Create your schedule</h2>
      </div>
      <p className="text-sm text-gray-500 font-semibold mb-6">
        Pick a theme and a granularity — the LLM will propose <strong>{count}</strong> cohesive sessions,
        which you'll then generate one by one (or in bulk).
      </p>

      <div className="space-y-5">
        <Field label="Theme" icon={<Sparkles className="w-4 h-4" />}>
          <textarea
            value={theme}
            onChange={(e) => setTheme(e.target.value)}
            placeholder="e.g. A year of general knowledge for 11-13 year-olds"
            rows={2}
            className="w-full px-4 py-3 rounded-xl border-2 border-lavender-200 bg-white/70 focus:border-sakura-400 focus:outline-none text-sm font-semibold text-gray-700 placeholder:text-gray-400 placeholder:font-normal"
          />
        </Field>

        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Granularity" icon={<Layers className="w-4 h-4" />}>
            <select
              value={granularity}
              onChange={(e) => {
                const g = e.target.value as Granularity;
                setGranularity(g);
                // Tant que l'utilisateur n'a pas touché au count, on suit la cadence naturelle.
                if (!countDirty) setCount(DEFAULT_COUNT_BY_GRANULARITY[g]);
              }}
              className="w-full px-3 py-2.5 rounded-xl border-2 border-lavender-200 bg-white/70 focus:border-sakura-400 focus:outline-none text-sm font-semibold text-gray-700"
            >
              <option value="weekly">Weekly (52 sessions = 1 year)</option>
              <option value="daily">Daily (30 sessions = 1 month)</option>
              <option value="monthly">Monthly (12 sessions = 1 year)</option>
              <option value="custom">Custom (explicit dates)</option>
            </select>
          </Field>
          <Field label="Number of sessions" icon={<Hash className="w-4 h-4" />}>
            <input
              type="number"
              min={1}
              max={60}
              value={count}
              onChange={(e) => {
                setCount(Math.max(1, Math.min(60, parseInt(e.target.value, 10) || 1)));
                setCountDirty(true);
              }}
              className="w-full px-3 py-2.5 rounded-xl border-2 border-lavender-200 bg-white/70 focus:border-sakura-400 focus:outline-none text-sm font-semibold text-gray-700"
            />
          </Field>
        </div>

        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Language" icon={<Languages className="w-4 h-4" />}>
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl border-2 border-lavender-200 bg-white/70 focus:border-sakura-400 focus:outline-none text-sm font-semibold text-gray-700"
            >
              {LANGUAGES.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </Field>
          <Field label="Difficulty">
            <select
              value={difficulty}
              onChange={(e) => setDifficulty(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl border-2 border-lavender-200 bg-white/70 focus:border-sakura-400 focus:outline-none text-sm font-semibold text-gray-700"
            >
              {DIFFICULTIES.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </Field>
        </div>

        {/* Anchor selon granularité */}
        <Field label="Starts at">
          {granularity === "weekly" && (
            <input
              type="number"
              min={1}
              max={53}
              value={startWeek}
              onChange={(e) => setStartWeek(Math.max(1, Math.min(53, parseInt(e.target.value, 10) || 1)))}
              className="w-full px-3 py-2.5 rounded-xl border-2 border-lavender-200 bg-white/70 focus:border-sakura-400 focus:outline-none text-sm font-semibold text-gray-700"
              placeholder="ISO week number"
            />
          )}
          {granularity === "monthly" && (
            <select
              value={startMonth}
              onChange={(e) => setStartMonth(parseInt(e.target.value, 10))}
              className="w-full px-3 py-2.5 rounded-xl border-2 border-lavender-200 bg-white/70 focus:border-sakura-400 focus:outline-none text-sm font-semibold text-gray-700"
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={m}>{new Date(2000, m - 1, 1).toLocaleString("en", { month: "long" })}</option>
              ))}
            </select>
          )}
          {(granularity === "daily" || granularity === "custom") && (
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl border-2 border-lavender-200 bg-white/70 focus:border-sakura-400 focus:outline-none text-sm font-semibold text-gray-700"
            />
          )}
        </Field>

        <motion.button
          whileHover={{ scale: 1.02, y: -2 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleGenerate}
          disabled={generating}
          className="w-full py-3 px-6 text-white font-black text-base bg-gradient-to-r from-sakura-400 via-sakura-500 to-lavender-400 rounded-xl shadow-kawaii-lg hover:shadow-kawaii disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
        >
          {generating ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              The LLM is drafting your sessions…
            </>
          ) : (
            <>
              <Wand2 className="w-5 h-5" />
              Generate outline
            </>
          )}
        </motion.button>
      </div>
    </motion.div>
  );
}

// --- Editor (schedule existante) ---

interface EditorProps {
  schedule: Schedule;
  onChanged: () => void;
  setError: (e: string) => void;
}

function Editor({ schedule, onChanged, setError }: EditorProps) {
  const [generatingId, setGeneratingId] = useState<string | null>(null);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingQuestionsFor, setEditingQuestionsFor] = useState<Session | null>(null);
  const [copied, setCopied] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);

  const shareUrl = `${window.location.origin}/auto-room?vitrine=${encodeURIComponent(schedule.userId)}`;

  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };

  const generateSession = async (sessionId: string) => {
    setGeneratingId(sessionId);
    setError("");
    try {
      const res = await adminFetch(`/api/admin/my-schedule/sessions/${encodeURIComponent(sessionId)}/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      if (!res) return; // session expirée : nav vers Authentik en cours
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.details || err.error || "Generation failed");
      }
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Generation failed");
    } finally {
      setGeneratingId(null);
    }
  };

  const bulkGenerate = async () => {
    setBulkRunning(true);
    setError("");
    const ungenerated = schedule.sessions.filter((s) => !s.generated);
    for (const s of ungenerated) {
      setGeneratingId(s.id);
      try {
        const res = await adminFetch(`/api/admin/my-schedule/sessions/${encodeURIComponent(s.id)}/generate`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({}),
        });
        if (!res) return; // session expirée : nav vers Authentik en cours (sort de la boucle bulk)
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.details || err.error || `Failed for ${s.label}`);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Bulk generation failed");
        break;
      }
    }
    setGeneratingId(null);
    setBulkRunning(false);
    onChanged();
  };

  const saveSessionMeta = async (sessionId: string, title: string, topic: string) => {
    const updated = {
      ...schedule,
      sessions: schedule.sessions.map((s) =>
        s.id === sessionId ? { ...s, title, topic } : s
      ),
    };
    try {
      const res = await adminFetch("/api/admin/my-schedule", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updated),
      });
      if (!res) return; // session expirée : nav vers Authentik en cours
      if (!res.ok) throw new Error("Save failed");
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    }
  };

  const resetSchedule = async () => {
    try {
      const cleared = { ...schedule, sessions: [] };
      const res = await adminFetch("/api/admin/my-schedule", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(cleared),
      });
      if (!res) return; // session expirée : nav vers Authentik en cours
      if (!res.ok) throw new Error("Reset failed");
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Reset failed");
    }
  };

  const generatedCount = schedule.sessions.filter((s) => s.generated).length;
  const ungeneratedCount = schedule.sessions.length - generatedCount;

  return (
    <motion.div
      initial={{ y: 10, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
    >
      {/* Header / méta + share */}
      <div className="bg-white/85 backdrop-blur-xl rounded-blob shadow-kawaii-lg border-2 border-sakura-200/50 p-6 mb-5">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
          <div>
            <h2 className="text-2xl font-black text-sakura-500 flex items-center gap-2">
              <CalendarDays className="w-7 h-7" />
              My Schedule
            </h2>
            <p className="text-sm text-gray-500 font-semibold mt-1">
              <span className="text-emerald-600">{generatedCount}</span>
              {" / "}
              <span>{schedule.sessions.length}</span>
              {" sessions generated · "}
              <span className="capitalize">{schedule.granularity}</span>
              {" · "}
              {schedule.language}
              {" · "}
              {schedule.difficulty}
            </p>
          </div>

          <div className="flex items-center gap-2">
            {ungeneratedCount > 0 && (
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={bulkGenerate}
                disabled={bulkRunning}
                className="px-4 py-2 rounded-full text-white font-black text-sm bg-gradient-to-r from-sakura-400 to-lavender-400 shadow-kawaii flex items-center gap-2 disabled:opacity-50"
              >
                {bulkRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                Generate all ({ungeneratedCount})
              </motion.button>
            )}
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => setConfirmReset(true)}
              className="px-3 py-2 rounded-full text-xs font-bold text-lavender-500 bg-lavender-100 hover:bg-lavender-200 transition-colors flex items-center gap-1"
              title="Clear all sessions to start over"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Reset
            </motion.button>
          </div>
        </div>

        {/* Share link */}
        <div className="bg-gradient-to-r from-sakura-50 to-lavender-50 rounded-xl p-3 flex items-center gap-3 border-2 border-sakura-100/50">
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold text-lavender-500 mb-0.5">Share link (kiosk mode)</p>
            <p className="text-sm font-semibold text-gray-700 truncate" title={shareUrl}>{shareUrl}</p>
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={copyShare}
            className="shrink-0 px-3 py-2 rounded-full text-xs font-black text-white bg-gradient-to-r from-sakura-400 to-lavender-400 shadow-kawaii flex items-center gap-1"
          >
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            {copied ? "Copied!" : "Copy"}
          </motion.button>
        </div>
      </div>

      {/* Sessions list */}
      <div className="space-y-3">
        {schedule.sessions.map((session, idx) => (
          <SessionRow
            key={session.id}
            session={session}
            index={idx}
            granularity={schedule.granularity}
            editing={editingId === session.id}
            generating={generatingId === session.id}
            onEdit={() => setEditingId(session.id)}
            onCancelEdit={() => setEditingId(null)}
            onSave={async (title, topic) => {
              await saveSessionMeta(session.id, title, topic);
              setEditingId(null);
            }}
            onGenerate={() => generateSession(session.id)}
            onEditQuestions={() => setEditingQuestionsFor(session)}
          />
        ))}
      </div>

      {/* Modal d'édition des questions générées */}
      <AnimatePresence>
        {editingQuestionsFor && (
          <QuestionsModal
            session={editingQuestionsFor}
            onClose={() => setEditingQuestionsFor(null)}
            onSaved={() => { setEditingQuestionsFor(null); onChanged(); }}
            setError={setError}
          />
        )}
      </AnimatePresence>

      {/* Confirm reset modal */}
      <AnimatePresence>
        {confirmReset && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setConfirmReset(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 10 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 10 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-blob shadow-kawaii-lg p-6 max-w-sm w-full border-2 border-sakura-200"
            >
              <h3 className="text-xl font-black text-sakura-500 mb-2">Reset schedule?</h3>
              <p className="text-sm text-gray-600 font-semibold mb-5">
                This clears all sessions (the wizard starts fresh). Already generated questions
                remain in your Question Sets.
              </p>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setConfirmReset(false)}
                  className="px-4 py-2 rounded-full text-sm font-bold text-gray-500 hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={() => { setConfirmReset(false); resetSchedule(); }}
                  className="px-4 py-2 rounded-full text-sm font-black text-white bg-gradient-to-r from-sakura-500 to-lavender-500 shadow-kawaii"
                >
                  Reset
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// --- SessionRow ---

interface SessionRowProps {
  session: Session;
  index: number;
  granularity: Granularity;
  editing: boolean;
  generating: boolean;
  onEdit: () => void;
  onCancelEdit: () => void;
  onSave: (title: string, topic: string) => void;
  onGenerate: () => void;
  onEditQuestions: () => void;
}

function SessionRow({
  session, index, granularity, editing, generating,
  onEdit, onCancelEdit, onSave, onGenerate, onEditQuestions,
}: SessionRowProps) {
  const [draftTitle, setDraftTitle] = useState(session.title);
  const [draftTopic, setDraftTopic] = useState(session.topic);

  useEffect(() => {
    setDraftTitle(session.title);
    setDraftTopic(session.topic);
  }, [session.title, session.topic, editing]);

  const dateBadge =
    granularity === "weekly" ? `W${session.weekNumber ?? "?"}` :
    granularity === "monthly" ? new Date(2000, (session.month ?? 1) - 1).toLocaleString("en", { month: "short" }) :
    session.date ?? session.label;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.02, 0.4) }}
      className={`bg-white/85 backdrop-blur-xl rounded-2xl shadow-kawaii-card border-2 p-4 flex items-start gap-3 ${
        session.generated ? "border-mint/40" : "border-lavender-100/70"
      }`}
    >
      <div className={`shrink-0 w-14 h-14 rounded-2xl flex flex-col items-center justify-center font-black text-xs ${
        session.generated ? "bg-mint/20 text-emerald-600" : "bg-lavender-100 text-lavender-500"
      }`}>
        <span className="text-[10px] opacity-70 uppercase">{granularity[0]}</span>
        <span className="text-sm leading-none">{dateBadge}</span>
      </div>

      <div className="flex-1 min-w-0">
        {editing ? (
          <div className="space-y-2">
            <input
              value={draftTitle}
              onChange={(e) => setDraftTitle(e.target.value)}
              placeholder="Title"
              className="w-full px-3 py-2 rounded-lg border-2 border-lavender-200 bg-white text-sm font-bold text-gray-700 focus:border-sakura-400 focus:outline-none"
            />
            <textarea
              value={draftTopic}
              onChange={(e) => setDraftTopic(e.target.value)}
              placeholder="Topic / description"
              rows={2}
              className="w-full px-3 py-2 rounded-lg border-2 border-lavender-200 bg-white text-sm text-gray-600 focus:border-sakura-400 focus:outline-none"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={onCancelEdit}
                className="px-3 py-1.5 rounded-full text-xs font-bold text-gray-500 hover:bg-gray-100 flex items-center gap-1"
              >
                <X className="w-3.5 h-3.5" /> Cancel
              </button>
              <button
                onClick={() => onSave(draftTitle, draftTopic)}
                className="px-3 py-1.5 rounded-full text-xs font-black text-white bg-gradient-to-r from-sakura-400 to-lavender-400 shadow-kawaii flex items-center gap-1"
              >
                <Save className="w-3.5 h-3.5" /> Save
              </button>
            </div>
          </div>
        ) : (
          <>
            <h3 className="font-black text-gray-700 truncate">{session.title || <em className="text-gray-400">No title</em>}</h3>
            <p className="text-xs text-gray-500 font-semibold leading-snug line-clamp-2">{session.topic}</p>
          </>
        )}
      </div>

      {!editing && (
        <div className="shrink-0 flex items-center gap-1">
          <button
            onClick={onEdit}
            className="p-2 rounded-full text-lavender-500 hover:bg-lavender-100 transition-colors"
            title="Edit title/topic"
          >
            <Edit3 className="w-4 h-4" />
          </button>
          {session.generated && (
            <button
              onClick={onEditQuestions}
              className="p-2 rounded-full text-emerald-600 hover:bg-mint/40 transition-colors"
              title="Edit questions"
            >
              <ListChecks className="w-4 h-4" />
            </button>
          )}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onGenerate}
            disabled={generating}
            className={`px-3 py-1.5 rounded-full text-xs font-black flex items-center gap-1 shadow-kawaii disabled:opacity-50 ${
              session.generated
                ? "bg-mint/30 text-emerald-700 hover:bg-mint/50"
                : "bg-gradient-to-r from-sakura-400 to-lavender-400 text-white"
            }`}
            title={session.generated ? "Regenerate questions" : "Generate questions"}
          >
            {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : session.generated ? <RefreshCw className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
            {session.generated ? "Regen" : "Generate"}
          </motion.button>
        </div>
      )}
    </motion.div>
  );
}

// --- Modal d'édition des questions générées ---

interface QuestionsModalProps {
  session: Session;
  onClose: () => void;
  onSaved: () => void;
  setError: (e: string) => void;
}

function QuestionsModal({ session, onClose, onSaved, setError }: QuestionsModalProps) {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Charger les questions du set sous-jacent (alias /api/public/questions/<setId>)
  useEffect(() => {
    if (!session.questionSetId) return;
    let cancelled = false;
    fetch(`/api/public/questions/${encodeURIComponent(session.questionSetId)}`)
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        setQuestions(Array.isArray(data.questions) ? data.questions : []);
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setError("Failed to load questions");
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [session.questionSetId, setError]);

  const updateQuestion = (idx: number, patch: Partial<Question>) => {
    setQuestions((qs) => qs.map((q, i) => (i === idx ? { ...q, ...patch } : q)));
    setDirty(true);
  };

  const updateOption = (qIdx: number, optIdx: number, value: string) => {
    setQuestions((qs) =>
      qs.map((q, i) => {
        if (i !== qIdx) return q;
        const opts = [...q.options];
        opts[optIdx] = value;
        return { ...q, options: opts };
      })
    );
    setDirty(true);
  };

  const removeQuestion = (idx: number) => {
    setQuestions((qs) => qs.filter((_, i) => i !== idx));
    setDirty(true);
  };

  const addQuestion = () => {
    setQuestions((qs) => [
      ...qs,
      { id: qs.length + 1, question: "", options: ["", "", ""], correctAnswer: 0 },
    ]);
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await adminFetch(
        `/api/admin/my-schedule/sessions/${encodeURIComponent(session.id)}/questions`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ questions }),
        }
      );
      if (!res) return; // session expirée : nav vers Authentik en cours
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.details || err.error || "Save failed");
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, y: 10 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.95, y: 10 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-blob shadow-kawaii-lg border-2 border-sakura-200 w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b-2 border-sakura-100 flex items-center justify-between gap-3 shrink-0">
          <div className="min-w-0">
            <h3 className="text-lg font-black text-sakura-500 truncate">{session.title || "Untitled session"}</h3>
            <p className="text-xs text-gray-500 font-semibold truncate">{session.label} · {questions.length} questions</p>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 p-2 rounded-full text-gray-400 hover:bg-gray-100 transition-colors"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          {loading ? (
            <div className="flex flex-col items-center py-10">
              <Loader2 className="w-8 h-8 text-sakura-500 animate-spin mb-2" />
              <p className="text-sm text-lavender-400 font-semibold">Loading questions…</p>
            </div>
          ) : (
            <>
              {questions.map((q, i) => (
                <QuestionCard
                  key={i}
                  index={i}
                  question={q}
                  onChange={(patch) => updateQuestion(i, patch)}
                  onChangeOption={(optIdx, value) => updateOption(i, optIdx, value)}
                  onRemove={() => removeQuestion(i)}
                />
              ))}
              <button
                onClick={addQuestion}
                className="w-full py-3 rounded-xl border-2 border-dashed border-lavender-200 text-lavender-400 hover:text-sakura-500 hover:border-sakura-300 transition-colors font-bold text-sm flex items-center justify-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add question
              </button>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t-2 border-sakura-100 flex items-center justify-between gap-2 shrink-0 bg-white">
          <p className="text-xs font-semibold text-gray-400">
            {dirty ? "Unsaved changes" : "No changes"}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-full text-sm font-bold text-gray-500 hover:bg-gray-100"
            >
              Cancel
            </button>
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={save}
              disabled={saving || !dirty}
              className="px-4 py-2 rounded-full text-sm font-black text-white bg-gradient-to-r from-sakura-400 to-lavender-400 shadow-kawaii flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save changes
            </motion.button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

interface QuestionCardProps {
  index: number;
  question: Question;
  onChange: (patch: Partial<Question>) => void;
  onChangeOption: (optIdx: number, value: string) => void;
  onRemove: () => void;
}

function QuestionCard({ index, question, onChange, onChangeOption, onRemove }: QuestionCardProps) {
  return (
    <div className="bg-gradient-to-br from-sakura-50/50 to-lavender-50/50 rounded-2xl p-4 border-2 border-sakura-100/60">
      <div className="flex items-start gap-3 mb-3">
        <span className="shrink-0 w-7 h-7 rounded-full bg-sakura-400 text-white font-black text-xs flex items-center justify-center">
          {index + 1}
        </span>
        <textarea
          value={question.question}
          onChange={(e) => onChange({ question: e.target.value })}
          rows={2}
          placeholder="Question text"
          className="flex-1 px-3 py-2 rounded-lg border-2 border-lavender-200 bg-white text-sm font-bold text-gray-700 focus:border-sakura-400 focus:outline-none"
        />
        <button
          onClick={onRemove}
          className="shrink-0 p-1.5 rounded-full text-red-400 hover:bg-red-50 transition-colors"
          title="Delete question"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      <div className="space-y-2 pl-10">
        {question.options.map((opt, i) => (
          <div key={i} className="flex items-center gap-2">
            <button
              onClick={() => onChange({ correctAnswer: i })}
              className={`shrink-0 w-7 h-7 rounded-full border-2 flex items-center justify-center text-xs font-black transition-colors ${
                question.correctAnswer === i
                  ? "bg-mint text-emerald-700 border-emerald-400"
                  : "bg-white text-gray-400 border-lavender-200 hover:border-sakura-300"
              }`}
              title={question.correctAnswer === i ? "Correct answer" : "Mark as correct"}
            >
              {String.fromCharCode(65 + i)}
            </button>
            <input
              value={opt}
              onChange={(e) => onChangeOption(i, e.target.value)}
              placeholder={`Option ${String.fromCharCode(65 + i)}`}
              className="flex-1 px-3 py-1.5 rounded-lg border-2 border-lavender-100 bg-white text-sm text-gray-600 focus:border-sakura-400 focus:outline-none"
            />
          </div>
        ))}
      </div>
    </div>
  );
}

// --- Helpers ---

function Field({ label, icon, children }: { label: string; icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-black text-lavender-500 uppercase tracking-wider mb-1.5 flex items-center gap-1">
        {icon}
        {label}
      </span>
      {children}
    </label>
  );
}

// Implémentation locale d'ISO week (cohérente avec getCurrentWeek() côté serveur)
function getCurrentIsoWeek(): number {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 1);
  const day = start.getDay() || 7;
  if (day !== 1) start.setDate(start.getDate() + (8 - day));
  const diff = Math.floor((now.getTime() - start.getTime()) / (7 * 24 * 60 * 60 * 1000));
  return Math.max(1, diff + 1);
}
